/**
 * Privacy Policy Page
 * GDPR Compliant Privacy Policy
 */

import { AnimatedBackground } from "@/components/ui/animated-background";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Shield, Lock, Eye, Trash2, Download, Mail } from "lucide-react";
import { motion } from "framer-motion";

export default function PrivacyPolicy() {
  return (
    <AnimatedBackground className="min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Badge variant="gradient" className="mb-4">
            <Shield className="h-4 w-4 mr-2 text-[var(--accent)]" />
            GDPR Compliant
          </Badge>
          <h1 className="mb-4 font-display text-4xl font-bold !tracking-display">Privacy Policy</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            Last updated: October 15, 2026
          </p>
        </motion.div>

        <div className="space-y-6">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Eye className="h-5 w-5" />
                <span>Introduction</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                Anti-Slavery Intelligence ("we", "our", or "us") is committed to protecting your privacy and personal data. 
                This Privacy Policy explains how we collect, use, store, and protect your information in compliance with the 
                General Data Protection Regulation (GDPR) and other applicable data protection laws.
              </p>
            </CardContent>
          </Card>

          {/* Data We Collect */}
          <Card>
            <CardHeader>
              <CardTitle>1. Data We Collect</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">1.1 Personal Information</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Email address (for authentication)</li>
                  <li>User ID (automatically generated)</li>
                  <li>Registration timestamp</li>
                  <li>Analysis usage data (count and dates)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">1.2 Analysis Data</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Statement metadata you provide (company name and reporting year)</li>
                  <li>Summary scores and recommendations generated by the analyzer (peer benchmark comparisons are calculated in your browser from published peer figures and are not stored)</li>
                  <li>Category-level attainment percentages so your team can revisit the findings</li>
                  <li>A cached copy of each analysis: the scores, the advice for each industry chosen, and the short AI-written assessment notes the advice is written from, which include excerpts from the statement (the notes stay on our servers and are not shown in the app)</li>
                </ul>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2">
                  Your saved summaries are visible only in your account. The cached copy of an analysis is keyed by the statement&apos;s fingerprint, not by who uploaded it, so the same PDF keeps its scores when assessed again with the same framework and model version. The PDF itself is not stored on our servers.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">1.3 Technical Data</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Browser type and version</li>
                  <li>Privacy-friendly, cookieless usage analytics (aggregate only)</li>
                  <li>Session information</li>
                </ul>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2">
                  To enforce the free guest limit, our server stores a keyed one-way hash of your network address together with a
                  count of that day&apos;s analyses. The address itself is not stored, and the record is deleted automatically a few
                  days later.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">1.4 Derived Fingerprints & Metadata</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Content fingerprint (SHA-256 hash of the PDF bytes) to detect identical statements</li>
                  <li>Locked score metadata such as model version, computation timestamp, and approximate document size</li>
                </ul>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-2">
                  We do not store the raw PDF you upload. The fingerprint is a pseudonymous identifier that cannot be reversed
                  into the original file.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Your Data */}
          <Card>
            <CardHeader>
              <CardTitle>2. How We Use Your Data</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li><strong>Authentication:</strong> To verify your identity and provide secure access</li>
                <li><strong>Service Provision:</strong> To process your modern slavery statement analyses</li>
                <li><strong>Quota Management:</strong> To track and enforce usage limits (10 analyses/month)</li>
                <li>
                  <strong>Score Locking & Consistency:</strong> To fingerprint identical statements, reuse a previously
                  computed score, and maintain an audit trail of model version and computation date
                </li>
                <li><strong>Communication:</strong> To send sign-in links and service updates</li>
                <li><strong>Improvement:</strong> To analyze usage patterns and improve our service</li>
                <li><strong>Legal Compliance:</strong> To comply with legal obligations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Legal Basis for Processing */}
          <Card>
            <CardHeader>
              <CardTitle>3. Legal Basis for Processing (GDPR Article 6)</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-2 text-sm">
                <li><strong>Consent:</strong> You provide consent when signing up via email link</li>
                <li><strong>Contract Performance:</strong> Processing necessary to provide our service</li>
                <li>
                  <strong>Legitimate Interests:</strong> Service improvement, fingerprinting duplicate statements for
                  locked-score consistency, fraud prevention, and security monitoring. You may object to this processing at
                  any time (see Section 5).
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Data Storage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="h-5 w-5 text-[var(--accent)]" />
                <span>4. Data Storage and Security</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">4.1 Storage Location</h3>
                <p className="text-sm">
                  Your data is stored in our dedicated Google Cloud Firestore database (<code>lancaster-asi</code>) and Cloud Functions
                  located in London, UK (europe-west2). Raw PDFs are never persisted—only the structured analysis output and
                  derived fingerprints are stored. Google Cloud is GDPR compliant and certified under the EU-U.S. Data Privacy Framework.
                </p>
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  LLM processing: the ASI scoring pipeline invokes Google’s Gemini via <strong>Vertex AI’s EU endpoint</strong>.
                  Requests are processed within EU member states. Content is used only to generate your results and is not used to train Google models.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">4.2 Security Measures</h3>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>End-to-end encryption for data in transit (HTTPS/TLS)</li>
                  <li>Encryption at rest for all stored data</li>
                  <li>Firebase Authentication with email verification</li>
                  <li>Firestore security rules enforcing user data isolation</li>
                  <li>Regular security audits and updates</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-2">4.3 Data Retention</h3>
                <p className="text-sm">
                  Account information and saved analyses remain while your account is active. Cached analyses in
                  <code>analysis_cache</code> (scores, advice and short assessment notes, keyed by fingerprint) are kept so the same PDF keeps its scores
                  with the same framework and model version, and to maintain an audit trail. The advice depends on the industry chosen, and the homepage
                  check runs again for each request. After a model update, older cached versions are no longer used; we delete them when you ask us to.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Your Rights (GDPR) */}
          <Card>
            <CardHeader>
              <CardTitle>5. Your Rights Under GDPR</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Eye className="h-4 w-4 mt-1 text-[var(--accent)]" />
                    <div>
                      <p className="font-semibold text-sm">Right to Access (Article 15)</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        View all personal data we hold about you
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Trash2 className="h-4 w-4 mt-1 text-[var(--accent)]" />
                    <div>
                      <p className="font-semibold text-sm">Right to Erasure (Article 17)</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        Delete your account and all associated data
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Download className="h-4 w-4 mt-1 text-[var(--accent)]" />
                    <div>
                      <p className="font-semibold text-sm">Right to Data Portability (Article 20)</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        Export your data in a machine-readable format
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Lock className="h-4 w-4 mt-1 text-[var(--accent)]" />
                    <div>
                      <p className="font-semibold text-sm">Right to Rectification (Article 16)</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        Correct inaccurate personal data
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Shield className="h-4 w-4 mt-1 text-[var(--accent)]" />
                    <div>
                      <p className="font-semibold text-sm">Right to Object (Article 21)</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        Object to certain types of processing
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Mail className="h-4 w-4 mt-1 text-[var(--accent)]" />
                    <div>
                      <p className="font-semibold text-sm">Right to Withdraw Consent</p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        Withdraw consent at any time
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <p className="text-sm">
                  To exercise any of these rights, sign in to your account or contact us at{" "}
                  <a href="mailto:m.gad1@lancaster.ac.uk" className="text-primary-600 hover:underline">
                    m.gad1@lancaster.ac.uk
                  </a>{" "}
                  and include the company name, report year, and (if available) the fingerprint shown on your analysis to
                  help us locate the relevant record quickly.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Third-Party Services */}
          <Card>
            <CardHeader>
              <CardTitle>6. Third-Party Services</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">6.1 Service Providers</h3>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Google Firebase & Firestore:</strong> Authentication, hosting, and database storage (Firestore database in London, UK)</li>
                    <li><strong>Google Cloud Functions:</strong> Secure processing environment for uploaded statements</li>
                    <li><strong>Google Vertex AI (Gemini):</strong> AI analysis orchestrated by the ASI scoring pipeline through Vertex AI’s <strong>EU endpoint</strong>; requests are processed within EU member states and are not used to train Google models</li>
                    <li><strong>Plausible Analytics:</strong> Privacy-friendly, cookieless usage analytics hosted in the EU (aggregate only)</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">6.2 Data Processing Agreements</h3>
                  <p className="text-sm">
                    We have Data Processing Agreements (DPAs) in place with all third-party processors to ensure 
                    GDPR compliance and appropriate safeguards for your data.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sub-processors */}
          <Card>
            <CardHeader>
              <CardTitle>7. Sub-processors & Infrastructure</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>
                We rely on a small, transparent set of infrastructure providers. Their roles, regions, and data-use commitments
                are documented in our{" "}
                <a href="https://github.com/MS-AI-Reporting/react-app-statement-reviewer/blob/main/SUBPROCESSORS.md" className="text-[var(--accent)] hover:underline">
                  Sub-processor Register
                </a>.
              </p>
              <p>
                Highlights:
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>Our application servers and database run in our Google Cloud project in London (europe-west2).</li>
                <li>Google Gemini (Vertex AI) processes content through its <strong>EU endpoint</strong>, within EU member states; content is not used to train models.</li>
                <li>Plausible provides cookieless, aggregate analytics hosted in the EU.</li>
              </ul>
            </CardContent>
          </Card>

          {/* Cookies */}
          <Card>
            <CardHeader>
              <CardTitle>8. Cookies and Local Storage</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-3">We use the following cookies and local storage:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li><strong>Authentication Cookies:</strong> To keep you signed in (essential)</li>
                <li><strong>Guest Usage Counter:</strong> To track free analysis usage (essential)</li>
                <li><strong>Analytics Measurement:</strong> Plausible uses cookieless, aggregate events only (no personal data stored)</li>
              </ul>
              <p className="text-sm mt-3">
                You can control cookie preferences in your browser settings.
              </p>
            </CardContent>
          </Card>

          {/* Automated Processing */}
          <Card>
            <CardHeader>
              <CardTitle>9. Automated Processing & Locked Scores</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <p>
                Analysis is performed automatically using Google Gemini. When the PDF you upload
                matches a previously analysed statement (same content fingerprint), we reuse the locked score and display the
                model version, lock date, and fingerprint in your results for transparency.
              </p>
              <p>
                If you update your statement or want a manual review, simply upload the new PDF (it will produce a new fingerprint)
                or contact us with the fingerprint so we can delete the cached copy and trigger a fresh analysis.
              </p>
            </CardContent>
          </Card>

          {/* FAQ */}
          <Card>
            <CardHeader>
              <CardTitle>10. FAQ: Does the AI train on our data?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <p>
                No. The ASI scoring pipeline orchestrates analysis via <strong>Google Vertex AI’s EU endpoint</strong>. Gemini processes
                the content to generate your results and does not use it to train models or for future product improvements.
              </p>
              <p>
                We store the summary scores and metadata in your account so you can revisit the findings, and a cached copy of the results
                (including short assessment notes with excerpts from the statement) under its fingerprint. The PDF is not stored, and you can delete summaries
                (or ask us to delete cached copies) at any time.
              </p>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle>11. Contact Us</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm mb-3">
                For questions about this Privacy Policy or to exercise your rights:
              </p>
              <div className="space-y-2 text-sm">
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:m.gad1@lancaster.ac.uk" className="text-primary-600 hover:underline">
                    m.gad1@lancaster.ac.uk
                  </a>{" "}
                  (please include the fingerprint from your report so we can locate the relevant record quickly).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Updates */}
          <Card>
            <CardHeader>
              <CardTitle>12. Policy Updates</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm">
                We may update this Privacy Policy from time to time. We will notify you of significant changes via 
                email or a prominent notice on our website. Your continued use of the service after changes constitutes 
                acceptance of the updated policy.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </AnimatedBackground>
  );
}
